import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { PLATFORM_LOGOS } from "@/components/PlatformLogos";
import { FoundingBadge } from "@/components/FoundingBadge";
import { useAuth } from "@/contexts/AuthContext";
import WikifiMascot from "@/components/WikifiMascot";

export default function Algorithm() {
  const { user } = useAuth();
  const [platforms, setPlatforms] = useState({});
  const [selected, setSelected] = useState("youtube");
  const [tab, setTab] = useState("url");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [ctr, setCtr] = useState("");
  const [retention, setRetention] = useState("");
  const [url, setUrl] = useState("");
  const [urlReport, setUrlReport] = useState(null);
  const [trends, setTrends] = useState([]);
  const [report, setReport] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(()=>{
    api.get("/algorhytm/platforms").then(r => setPlatforms(r.data));
    api.get("/algorhytm/trends").then(r => setTrends(r.data));
  }, []);

  const run = async () => {
    if (!title.trim()) return;
    setLoading(true); setReport(null);
    try {
      const r = await api.post("/algorhytm/optimize", { platform: selected, title, description: desc });
      setReport(r.data);
    } finally { setLoading(false); }
  };

  const runDiagnose = async () => {
    if (!title.trim()) return;
    setLoading(true); setDiagnosis(null);
    try {
      const metrics = {};
      if (ctr) metrics.ctr = parseFloat(ctr);
      if (retention) metrics.retention_pct = parseFloat(retention);
      const r = await api.post("/algorhytm/diagnose", { platform: selected, title, description: desc, metrics });
      setDiagnosis(r.data);
    } finally { setLoading(false); }
  };

  const runUrl = async () => {
    if (!url.trim()) return;
    setLoading(true); setUrlReport(null); setErr(null);
    try {
      const r = await api.post("/algorhytm/analyze-url", { url });
      setUrlReport(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Analysis failed.");
    } finally { setLoading(false); }
  };

  const active = platforms[selected];

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="algorithm-hero"
        variant="bare"
        badge="Wikifi"
        eyebrow="The Algorithm"
        title="Crack the algorithm."
        sub="Demystifying how each major platform's recommendation system ranks content in 2026 — and how to get yours in front of a mainstream audience."
      />

      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-10 gap-2 mb-6" data-testid="platform-selector">
        {Object.entries(PLATFORM_LOGOS).map(([k, p]) => {
          // Merge with API-provided platforms — if the API doesn't return this key
          // still show it, but disable selection
          const apiInfo = platforms[k];
          const Logo = p.Logo;
          const active = selected === k;
          return (
            <button
              key={k}
              onClick={() => { setSelected(k); setReport(null); setDiagnosis(null); }}
              disabled={!apiInfo}
              className={`group flex flex-col items-center gap-1.5 p-2.5 rounded-xl bg-white border transition-all ${
                active
                  ? "border-[#0B1220] shadow-sm -translate-y-0.5"
                  : "border-[rgba(220,226,236,0.9)] hover:border-[#0B1220]/40 hover:shadow-sm hover:-translate-y-0.5"
              } ${!apiInfo ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`platform-${k}`}
              title={apiInfo ? p.name : `${p.name} — coming soon`}
            >
              <Logo size={30} />
              <div className="font-semibold text-[11px] text-[#0B1220] text-center leading-tight">
                {p.name}
              </div>
              {!apiInfo && (
                <span className="text-[8px] uppercase tracking-wider font-bold text-[#8A93A6]">Soon</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide pb-2" data-testid="algorithm-tabs">
        <button onClick={()=>setTab("url")} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${tab==='url' ? 'bg-[var(--curio-navy)] text-white' : 'btn-ghost'}`} data-testid="tab-url">Analyze URL</button>
        <button onClick={()=>setTab("optimize")} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${tab==='optimize' ? 'bg-[var(--curio-navy)] text-white' : 'btn-ghost'}`} data-testid="tab-optimize">Optimize new content</button>
        <button onClick={()=>setTab("diagnose")} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${tab==='diagnose' ? 'bg-[var(--curio-navy)] text-white' : 'btn-ghost'}`} data-testid="tab-diagnose">Diagnose underperforming</button>
        <button onClick={()=>setTab("trends")} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${tab==='trends' ? 'bg-[var(--curio-navy)] text-white' : 'btn-ghost'}`} data-testid="tab-trends">Viral trends</button>
      </div>

      {tab === "url" && (
        <div className="curio-card p-6" data-testid="url-analyzer">
          <h2 className="font-display text-lg font-bold text-[#0B1220] mb-2 tracking-tight">Paste a link from any active platform</h2>
          <p className="text-sm text-[var(--curio-text-muted)] mb-4">Works with YouTube (video or channel), YouTube Shorts, Instagram Reels, and TikTok. YouTube pulls real metadata; Shorts / Reels / TikTok get a URL-only 2026 ranker read (add title + description in Diagnose for the deepest analysis). Free tier: 10 analyses per month.</p>
          <div className="flex gap-2 mb-3">
            <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="youtube.com/… · youtube.com/shorts/… · instagram.com/reel/… · tiktok.com/@user/video/…" className="flex-1 bg-[var(--curio-bg)] border border-[var(--curio-border)] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--curio-navy)]" data-testid="url-input"/>
            <button onClick={runUrl} disabled={loading || !url.trim()} className="btn-coral disabled:opacity-50" data-testid="url-run">
              {loading ? <><Loader2 size={14} className="animate-spin"/> Analyzing…</> : <><Sparkles size={14}/> Analyze</>}
            </button>
          </div>
          {err && <div className="text-sm text-rose-600 mb-4">{err} {err.includes("Sign in") && <a href="/login" className="underline">Sign in</a>} {err.includes("Upgrade") && <a href="/pricing" className="underline">Upgrade</a>}</div>}

          {urlReport && (
            <div className="mt-6 fade-in" data-testid="url-report">
              {user?.founding_number && (
                <div className="mb-4 flex items-center gap-2" data-testid="url-report-founding">
                  <FoundingBadge number={user.founding_number} size="md"/>
                  <span className="text-[11px] text-[#5B6478]">Your permanent Founding status is stamped on every report you run.</span>
                </div>
              )}
              <div className="flex items-start gap-6 mb-6">
                {urlReport.thumbnail && <img src={urlReport.thumbnail} alt="thumb" className="w-48 rounded-xl"/>}
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold mb-1">{urlReport.metadata.mode} · {urlReport.metadata.views ? Number(urlReport.metadata.views).toLocaleString() + " views" : "channel"}</div>
                  <h3 className="font-display text-lg font-bold text-[#0B1220] mb-1 tracking-tight">{urlReport.metadata.title}</h3>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="text-center">
                      <div className="font-display text-3xl font-bold text-[#F97316]">{urlReport.report.virality_score}</div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478]">Virality Score</div>
                    </div>
                  </div>
                </div>
              </div>

              {urlReport.report.score_breakdown && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  {Object.entries(urlReport.report.score_breakdown).map(([k,v]) => (
                    <div key={k} className="curio-card p-3 text-center">
                      <div className="font-display text-xl font-bold text-[#0B1220]">{v}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#5B6478] font-bold mt-1">{k}</div>
                    </div>
                  ))}
                </div>
              )}

              <p className="font-display text-base italic mb-5 border-l-4 border-[#F97316] pl-4 text-[#0B1220]">"{urlReport.report.verdict}"</p>

              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div className="curio-card p-5">
                  <div className="text-xs uppercase tracking-widest font-bold mb-2">Improved titles</div>
                  <ul className="space-y-1.5 text-sm">{urlReport.report.title_suggestions?.map((t,i)=><li key={i}>· {t}</li>)}</ul>
                </div>
                <div className="curio-card p-5">
                  <div className="text-xs uppercase tracking-widest font-bold mb-2">Thumbnail direction</div>
                  <p className="text-sm">{urlReport.report.thumbnail_direction}</p>
                  <div className="text-xs uppercase tracking-widest font-bold mt-4 mb-2">Best posting window</div>
                  <p className="text-sm">{urlReport.report.best_posting_window}</p>
                </div>
              </div>

              <div className="curio-card p-5 mb-5">
                <div className="text-xs uppercase tracking-widest font-bold mb-2">Description rewrite</div>
                <p className="text-sm leading-relaxed">{urlReport.report.description_fix}</p>
              </div>

              <div className="rounded-xl p-5 bg-[#F7F8FA] border border-[#E7EAF0]">
                <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold mb-2">Prioritized checklist</div>
                <ul className="space-y-1.5">
                  {urlReport.report.checklist?.map((c,i)=><li key={i} className="flex gap-2 text-sm text-[#0B1220]"><CheckCircle2 size={14} className="text-[#F97316] flex-shrink-0 mt-0.5"/>{c}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "trends" && (
        <div data-testid="viral-trends">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold mb-2">Weekly · {trends[0]?.week || "2026"}</div>
          <h2 className="font-display text-xl font-bold text-[#0B1220] mb-4 tracking-tight">This week's viral formats</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
            {trends.map((t, i) => (
              <div key={i} className="curio-card p-5">
                <div className="text-[10px] uppercase tracking-widest text-[#F97316] font-bold mb-1">{t.platform}</div>
                <h3 className="font-display text-base font-bold text-[#0B1220] mb-2 tracking-tight">{t.format}</h3>
                <p className="text-sm text-[#5B6478] mb-2"><strong className="text-[#0B1220]">Why it works:</strong> {t.why}</p>
                <p className="text-sm"><strong className="text-[#F97316]">Apply to your niche:</strong> {t.apply}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {active && tab === "optimize" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="curio-card p-6">
            <h2 className="font-display text-lg font-bold text-[#0B1220] mb-2 tracking-tight">{active.name} ranking signals</h2>
            <p className="text-sm text-[#0B1220]/80 leading-relaxed mb-4">{active.tldr}</p>
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold mb-2">Top 3 signals</div>
            <ul className="space-y-1.5 mb-5">
              {active.top_3.map((s,i)=>(<li key={i} className="flex gap-2 text-sm"><span className="text-[#F97316] font-bold">{i+1}.</span>{s}</li>))}
            </ul>
            <div className="text-xs uppercase tracking-widest text-[#5B6478] font-bold mb-2">All signals tracked</div>
            <div className="flex flex-wrap gap-1.5">
              {active.primary_signals.map(s => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#F7F9FC] border border-[#E3E8F0]">{s}</span>)}
            </div>
          </div>

          <div className="curio-card p-6" data-testid="optimization-form">
            <h2 className="font-display text-lg font-bold text-[#0B1220] mb-4 tracking-tight">Get your optimization report</h2>
            <label className="text-xs uppercase tracking-widest font-bold text-[#5B6478] block mb-1">Content idea / title</label>
            <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="How I made $10k in 30 days with AI" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-4 py-2.5 mb-4 text-sm focus:outline-none focus:border-[#0B1220]" data-testid="algorithm-title-input"/>
            <label className="text-xs uppercase tracking-widest font-bold text-[#5B6478] block mb-1">Description (optional)</label>
            <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={3} placeholder="Short pitch of your content…" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-4 py-2.5 mb-4 text-sm focus:outline-none focus:border-[#0B1220]" data-testid="algorithm-desc-input"/>
            <button onClick={run} disabled={loading || !title.trim()} className="btn-coral w-full justify-center disabled:opacity-50" data-testid="algorithm-run-btn">
              {loading ? <><Loader2 size={14} className="animate-spin"/> Cracking the algorithm…</> : <><Sparkles size={14}/> Generate report</>}
            </button>
          </div>
        </div>
      )}

      {active && tab === "diagnose" && (
        <div className="curio-card p-6" data-testid="diagnose-form">
          <h2 className="font-display text-lg font-bold text-[#0B1220] mb-2 tracking-tight">Why isn't your {active.name} content seen?</h2>
          <p className="text-sm text-[#5B6478] mb-4">Paste your title and any metrics you have. We'll diagnose the exact algorithmic root cause.</p>
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Your published title" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-4 py-2.5 mb-3 text-sm focus:outline-none focus:border-[#0B1220]" data-testid="diag-title-input"/>
          <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={2} placeholder="Short description or context…" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-4 py-2.5 mb-3 text-sm focus:outline-none focus:border-[#0B1220]"/>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div><label className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1">CTR (%)</label><input value={ctr} onChange={e=>setCtr(e.target.value)} type="number" step="0.1" placeholder="e.g., 3.2" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm" data-testid="diag-ctr"/></div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1">Retention (%)</label><input value={retention} onChange={e=>setRetention(e.target.value)} type="number" step="1" placeholder="e.g., 42" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm" data-testid="diag-retention"/></div>
          </div>
          <button onClick={runDiagnose} disabled={loading || !title.trim()} className="btn-coral w-full justify-center disabled:opacity-50" data-testid="diag-run-btn">
            {loading ? <><Loader2 size={14} className="animate-spin"/> Diagnosing…</> : <><Sparkles size={14}/> Diagnose</>}
          </button>

          {diagnosis && (
            <div className="mt-6 fade-in" data-testid="diagnosis-report">
              {user?.founding_number && (
                <div className="mb-3">
                  <FoundingBadge number={user.founding_number} size="md"/>
                </div>
              )}
              <div className="text-xs uppercase tracking-widest font-bold text-[#F97316] mb-2">Verdict · predicted lift: {diagnosis.report.predicted_lift}</div>
              <p className="font-display text-lg italic mb-4 text-[#0B1220] tracking-tight">{diagnosis.report.verdict}</p>
              <div className="mb-4">
                <div className="text-xs uppercase tracking-widest font-bold mb-2">Root causes</div>
                <ul className="space-y-2">{diagnosis.report.root_causes?.map((r,i)=>(<li key={i} className="border-l-2 border-[#F97316] pl-3"><strong>{r.signal}:</strong> <span className="text-sm">{r.why}</span></li>))}</ul>
              </div>
              <div className="mb-4">
                <div className="text-xs uppercase tracking-widest font-bold mb-2">Fixes</div>
                <ul className="space-y-1.5 text-sm">{diagnosis.report.fixes?.map((f,i)=><li key={i}>→ {f}</li>)}</ul>
              </div>
              <div className="rounded-xl p-4 bg-[#F7F8FA] border border-[#E7EAF0]">
                <div className="text-xs uppercase tracking-widest font-bold text-[#F97316] mb-1">Rewrite</div>
                <p className="font-display text-lg tracking-tight text-[#0B1220]">"{diagnosis.report.rewrite}"</p>
              </div>
            </div>
          )}
        </div>
      )}

      {report && (
        <div className="mt-6 curio-card p-6 fade-in" data-testid="algorithm-report">
          {user?.founding_number && (
            <div className="mb-3">
              <FoundingBadge number={user.founding_number} size="md"/>
            </div>
          )}
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold mb-2">Optimization report · {report.platform.name}</div>
          <h3 className="font-display text-xl font-bold text-[#0B1220] mb-5 tracking-tight">"{report.report.hook}"</h3>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold mb-2">Title suggestions</div>
              <ul className="space-y-1.5 text-sm">{report.report.title_suggestions?.map((t,i)=><li key={i}>· {t}</li>)}</ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold mb-2">Thumbnail / cover idea</div>
              <p className="text-sm">{report.report.thumbnail_idea}</p>
            </div>
          </div>

          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest font-bold mb-2">Posting strategy</div>
            <p className="text-sm">{report.report.posting_strategy}</p>
          </div>

          <div className="mb-6 rounded-xl p-5 bg-[#F7F8FA] border border-[#E7EAF0]">
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold mb-2">Signals to engineer for</div>
            <ul className="space-y-1.5 text-sm text-[#0B1220]">{report.report.signals_focus?.map((s,i)=><li key={i}>→ {s}</li>)}</ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-bold mb-2">Checklist before posting</div>
            <ul className="space-y-1.5">
              {report.report.checklist?.map((c,i)=>(<li key={i} className="flex gap-2 text-sm"><CheckCircle2 size={15} className="text-[#F97316] flex-shrink-0 mt-0.5"/>{c}</li>))}
            </ul>
          </div>
        </div>
      )}
    </Layout>
  );
}
